import React, { useState, useEffect } from 'react';
import { Select, MenuItem, Box } from '@mui/material';

// Special option constants
const SELECT_ALL_VALUE = '__SELECT_ALL__';
const REMOVE_ALL_VALUE = '__REMOVE_ALL__';

/**
 * Reusable multi-select filter component with "Select All" and "Remove All" options
 * Features:
 * - Bulk selection actions (Select All/Remove All)
 * - Deferred updates (only applies changes when dropdown closes)
 * - Prevents dropdown from closing on option selection
 * - Shows selection count in the display value
 */
const ItemImageReviewMultiSelect = ({
    filterId,
    options = [],
    selectedValues = [],
    onUpdateFilter,
    isLoading = false
}) => {
    const hasSelectAllOption = options.length >= 2;
    const [isOpen, setIsOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState([]);
    
    useEffect(() => {
        if (!isOpen) {
            setPendingValues(selectedValues);
        }
    }, [selectedValues, isOpen]);
    
    // Initialize pending values when opening
    const handleOpen = () => {
        setIsOpen(true);
        setPendingValues(selectedValues);
    };
    
    const handleChange = (e) => {
        const value = e.target.value;
        
        // Filter out any special values that might have been included
        const filteredValues = Array.isArray(value) 
            ? value.filter(v => v !== SELECT_ALL_VALUE && v !== REMOVE_ALL_VALUE)
            : value;
        
        setPendingValues(filteredValues);
    };

    const handleClose = (e, reason) => {
        if (reason !== 'selectOption') {
            // Apply the pending changes when closing
            onUpdateFilter(filterId, pendingValues.length > 0 ? pendingValues : undefined);
            setIsOpen(false);
        }
    };

    const handleSelectAll = (e) => {
        e.stopPropagation();
        const allValues = options.map(option => option.value);
        setPendingValues(allValues);
    };

    const handleRemoveAll = (e) => {
        e.stopPropagation();
        setPendingValues([]);
    };
    
    return (
        <Box sx={{ minWidth: 200 }}>
            <Select
                multiple
                open={isOpen}
                onOpen={handleOpen}
                onClose={handleClose}
                value={pendingValues}
                onChange={handleChange}
                size="small"
                displayEmpty
                disabled={isLoading}
                renderValue={(selected) => {
                    // Filter out special values for display
                    const displayValues = selected.filter(v => v !== SELECT_ALL_VALUE && v !== REMOVE_ALL_VALUE);
                    if (displayValues.length === 0) return 'All';
                    if (displayValues.length === options.length) return 'All Selected';
                    return `${displayValues.length} selected`;
                }}
            >
                {hasSelectAllOption && (
                    <>
                        <MenuItem 
                            key={SELECT_ALL_VALUE} 
                            value={SELECT_ALL_VALUE}
                            onClick={handleSelectAll}
                            sx={{ 
                                fontWeight: 'bold',
                                mb: 0.5
                            }}
                        >
                            Select All
                        </MenuItem>
                        <MenuItem 
                            key={REMOVE_ALL_VALUE} 
                            value={REMOVE_ALL_VALUE}
                            onClick={handleRemoveAll}
                            sx={{ 
                                fontWeight: 'bold', 
                                borderBottom: '2px solid',
                                borderColor: 'divider',
                                mb: 0.5
                            }}
                        >
                            Remove All
                        </MenuItem>
                    </>
                )}
                {options.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
};

export default ItemImageReviewMultiSelect;